import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Star } from "lucide-react";
import { getReviews, postReview } from "@/lib/site";
import { Reveal } from "@/components/Reveal";

export function Stars({ value, size = "h-4 w-4" }) {
  return (
    <span className="flex gap-0.5" aria-label={`${value} ud af 5 stjerner`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= Math.round(value) ? "fill-pale text-pale" : "text-sage/40"}`}
        />
      ))}
    </span>
  );
}

export default function Reviews({ slug }) {
  const [data, setData] = useState({ reviews: [], average: null, count: 0 });
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    getReviews(slug).then(setData).catch(() => {});
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) return toast.error("Udfyld navn og anmeldelse");
    setSending(true);
    try {
      await postReview(slug, { name: name.trim(), rating, text: text.trim() });
      setSent(true);
      toast.success("Tak for din anmeldelse!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke sende anmeldelsen");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";

  return (
    <Reveal>
      <section data-testid="reviews-section" className="mt-20 border-t border-white/10 pt-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-serif text-3xl tracking-tight text-cream">Kunde-anmeldelser</h2>
          {data.count > 0 && (
            <p data-testid="reviews-average" className="flex items-center gap-3 text-sm text-sage">
              <Stars value={data.average} />
              <span className="font-mono text-xs tracking-[0.15em]">
                {data.average} · {data.count} {data.count === 1 ? "anmeldelse" : "anmeldelser"}
              </span>
            </p>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="space-y-6">
            {data.reviews.length === 0 && (
              <p className="text-sm text-sage">
                Ingen anmeldelser endnu — del din erfaring som den første.
              </p>
            )}
            {data.reviews.map((r) => (
              <div
                key={r.id}
                data-testid={`review-${r.id}`}
                className="border border-white/10 bg-moss p-6"
              >
                <div className="flex items-center justify-between gap-4">
                  <Stars value={r.rating} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage/60">
                    {(r.created_at || "").slice(0, 10)}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-cream/90">{r.text}</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-pale">
                  {r.name}
                </p>
              </div>
            ))}
          </div>

          <div className="border border-white/10 bg-moss p-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-pale">
              Skriv en anmeldelse
            </p>
            {sent ? (
              <p data-testid="review-thanks" className="mt-6 text-sm leading-relaxed text-sage">
                Tak! Din anmeldelse vises på siden, så snart vi har godkendt den.
              </p>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="review-form">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`review-star-${n}`}
                      onClick={() => setRating(n)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`h-6 w-6 ${n <= rating ? "fill-pale text-pale" : "text-sage/40"}`}
                      />
                    </button>
                  ))}
                </div>
                <input
                  data-testid="review-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dit navn"
                  className={field}
                />
                <textarea
                  data-testid="review-text-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  placeholder="Hvordan var din oplevelse?"
                  className={`${field} resize-y`}
                />
                <button
                  type="submit"
                  data-testid="review-submit-button"
                  disabled={sending}
                  className="flex items-center gap-3 rounded-full bg-forest px-7 py-3 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send anmeldelse"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </Reveal>
  );
}
